// ───────────────────────────────────────────────────────────────────────────
// Cross-tab login persistence.
//
// The app stores the logged-in user under sessionStorage["authUser"] (+ a
// "permissions" bundle). sessionStorage is PER-TAB, so opening the app in a new
// tab showed a login screen and even let a DIFFERENT user sign in there. We want
// a new tab to inherit the login that is already active, and to stay logged in
// as long as the user has not logged out.
//
// Rather than migrate ~50 files from sessionStorage to localStorage, we mirror
// just the auth keys into localStorage and:
//   1) on load, hydrate this tab's sessionStorage from the mirror (new tab gets
//      the existing login), and
//   2) transparently keep the mirror in sync by wrapping setItem/removeItem for
//      those keys, and
//   3) react to cross-tab logout via the storage event.
//
// This file must be imported FIRST in index.js, before anything reads auth.
// ───────────────────────────────────────────────────────────────────────────

const MIRRORED_KEYS = ["authUser", "permissions"];
const LS_PREFIX = "persist:"; // localStorage mirror key prefix

(function initCrossTabAuth() {
    if (typeof window === "undefined") return;

    try {
        // 1) Hydrate this (possibly new) tab from the localStorage mirror when the
        //    tab has no auth of its own yet.
        const hasSessionAuth = !!sessionStorage.getItem("authUser");
        const mirroredAuth = localStorage.getItem(LS_PREFIX + "authUser");
        if (!hasSessionAuth && mirroredAuth) {
            MIRRORED_KEYS.forEach((k) => {
                const v = localStorage.getItem(LS_PREFIX + k);
                if (v != null) sessionStorage.setItem(k, v);
            });
        } else if (hasSessionAuth) {
            // This tab logged in (or already had auth) — make sure the mirror
            // reflects it so OTHER new tabs can inherit.
            MIRRORED_KEYS.forEach((k) => {
                const v = sessionStorage.getItem(k);
                if (v != null) localStorage.setItem(LS_PREFIX + k, v);
            });
        }
    } catch (e) {
        // storage may be unavailable (private mode / disabled) — fail open.
        // The app simply behaves as before (per-tab login).
        // eslint-disable-next-line no-console
        console.warn("Cross-tab auth init skipped:", e);
    }

    // 2) Keep the localStorage mirror in sync with future sessionStorage writes
    //    for the auth keys, without touching the 50 call sites.
    try {
        const origSet = sessionStorage.setItem.bind(sessionStorage);
        const origRemove = sessionStorage.removeItem.bind(sessionStorage);

        sessionStorage.setItem = function (key, value) {
            origSet(key, value);
            if (MIRRORED_KEYS.includes(key)) {
                try { localStorage.setItem(LS_PREFIX + key, value); } catch (e) { /* ignore */ }
            }
        };
        sessionStorage.removeItem = function (key) {
            origRemove(key);
            if (MIRRORED_KEYS.includes(key)) {
                // Removing authUser is a logout — clear the whole mirror so other
                // tabs don't re-hydrate a stale session.
                try {
                    if (key === "authUser") {
                        MIRRORED_KEYS.forEach((k) => localStorage.removeItem(LS_PREFIX + k));
                    } else {
                        localStorage.removeItem(LS_PREFIX + key);
                    }
                } catch (e) { /* ignore */ }
            }
        };

        // logout calls sessionStorage.clear() — wipe the auth mirror too so a new
        // tab does NOT re-hydrate a logged-out session.
        const origClear = sessionStorage.clear.bind(sessionStorage);
        sessionStorage.clear = function () {
            origClear();
            try {
                MIRRORED_KEYS.forEach((k) => localStorage.removeItem(LS_PREFIX + k));
            } catch (e) { /* ignore */ }
        };
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Cross-tab auth sync hook skipped:", e);
    }

    // 3) Cross-tab logout: if another tab clears the mirror, this tab follows on
    //    its next navigation. We listen for the storage event and, if the mirror
    //    was cleared, drop this tab's session too.
    try {
        window.addEventListener("storage", (ev) => {
            // Another tab logged out (mirror authUser removed). Clear this tab's
            // session AND force a real navigation to the login page, so React
            // fully re-initialises in a logged-out state instead of leaving the
            // old authenticated view rendered (blocked but not logged out).
            const mirrorCleared = ev.key === LS_PREFIX + "authUser" && ev.newValue == null;
            // Some browsers fire a single event with key === null on clear().
            const wholeStoreCleared = ev.key === null;
            if (mirrorCleared || wholeStoreCleared) {
                try {
                    sessionStorage.removeItem("authUser");
                    sessionStorage.removeItem("permissions");
                } catch (e) { /* ignore */ }
                // Only redirect if this tab still thinks it's on an app page
                // (avoid bouncing a tab that's already on /login).
                try {
                    const path = window.location.pathname || "";
                    if (!/\/login\b/i.test(path)) {
                        window.location.assign("/login");
                    }
                } catch (e) { /* ignore */ }
            }
        });
    } catch (e) { /* ignore */ }
})();
